import {
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MessageSquare, ThumbsUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CtaCommunityOverlayProps } from "./CtaCommunityOverlay.types";
import { useAnnouncement } from "../../components/Announcement/Announcement.component";
import { useLikePostQuery, useLikeQuery, useLikeDeleteQuery } from "../../queries/community";
import { useAuthStore } from "../../stores";
import { ButtonDigited } from "../Button/Button.variants";

export function CtaCommunityOverlay({
  comments,
  id,
  likes,
  commentsOpened,
  commentsClickAction,
  selectedFilter,
  selectedTopic
} : CtaCommunityOverlayProps & {
  commentsOpened: boolean,
  commentsClickAction: CallableFunction
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { announce } = useAnnouncement();

  const ctaButtonSx = {
    padding: "16px 12px !important",
    "&.Mui-focusVisible": {
      outline: `2px solid ${theme.customColors.borderPrimaryCta}`,
      outlineOffset: "2px",
      borderRadius: "2px"
    }
  };

  const { mutate: mutateLike } = useLikePostQuery({
    entityId: id,
    selectedFilter:selectedFilter,
    selectedTopic:selectedTopic

  });
  const { mutate: mutateDeleteLike } = useLikeDeleteQuery({
    entityId: id,
    selectedFilter:selectedFilter,
    selectedTopic:selectedTopic
  });


  const userId = useAuthStore(state => state.userData?.id_user);

  const rootRef = useRef<HTMLDivElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (hasBeenVisible || !rootRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  const { data: likeData, isLoading } = useLikeQuery({
    enabled: hasBeenVisible,
    entityId: id ?? 0,
    userId: userId?.toString() ?? ""
  });

  // console.log("likeData: ", likeData);

  const liked = !isLoading && !!likeData?.data.find((likeInfo) => likeInfo.user_id == (userId?.toString() ?? ""));

  const [optimistic, setOptimistic] = useState<{ likes: number, liked: boolean } | null>(null);

  useEffect(() => {
    setOptimistic(null);
  }, [likes]);

  const displayedLiked = optimistic?.liked ?? liked;
  const displayedLikes = optimistic?.likes ?? Number(likes ?? 0);
  const likeBorderColor = displayedLiked
    ? theme.customColors.borderPrimaryCta
    : theme.customColors.textPrimary;

  return (
    <Stack
      ref={ rootRef }
      direction={ "row" }
      alignItems={ "center" }
      gap={ "1rem" }
    >
      <ButtonDigited
        type={ "button" }
        typology={ "secondary" }
        buttonSize={ "sm" }
        aria-label={ displayedLiked ? `${t("remove")} ${t("like")}` :`${t("apply")} ${t("like")}` }
        onClick={ () => {
          if(displayedLiked) {
            setOptimistic({ liked: false, likes: Math.max(0, displayedLikes - 1) });
            mutateDeleteLike(undefined, { onError: () => setOptimistic(null) });
            announce(t("announcement_like_removed"));
          } else {
            setOptimistic({ liked: true, likes: displayedLikes + 1 });
            mutateLike(undefined, { onError: () => setOptimistic(null) });
            announce(t("announcement_like_applied"));
          }
        } }
        sx={ {
          ...ctaButtonSx,
          borderColor: `${likeBorderColor} !important`
        } }
      >
        <>
          <ThumbsUp size={ "1rem" }/>
          <Typography 
            color={ theme.customColors.exploreCardText }
            fontSize={ "0.75rem" }
            fontWeight={ 600 }
          >
            { displayedLikes }
          </Typography>
        </>
      </ButtonDigited>

      <ButtonDigited
        type={ "button" }
        typology={ "secondary" }
        buttonSize={ "sm" }
        aria-label={ t("open_comments") }
        onClick={ () => commentsClickAction() }
        sx={ {
          ...ctaButtonSx,
          borderColor: `${commentsOpened ? theme.customColors.borderPrimaryCta : theme.customColors.textPrimary} !important`
        } }
      >
        <>
          <MessageSquare size={ "1rem" }/>
          <Typography 
            color={ theme.customColors.exploreCardText }
            fontSize={ "0.75rem" }
            fontWeight={ 600 }
          >
            { comments }
          </Typography>
        </>
      </ButtonDigited>
    </Stack>
  );

}
